import styled, { css } from 'styled-components';

// Estilo do chip de variável `{{...}}`, compartilhado entre o editor do body e
// o editor do subject.
export const templateVariableChipCss = css`
  .template-variable {
    display: inline-block;
    padding: 0 0.4rem;
    margin: 0 1px;
    border-radius: 4px;
    background: ${({ theme }) => theme.grayDarker};
    color: ${({ theme }) => theme.pantone657C};
    font-family: 'Courier New', monospace;
    font-size: 0.875em;
    font-weight: 600;
    user-select: all;
    white-space: nowrap;
    cursor: default;
  }

  .ProseMirror-selectednode .template-variable {
    outline: 2px solid ${({ theme }) => theme.pantone2726C};
    outline-offset: 1px;
  }
`;

export const PageLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const EditorColumn = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    order: 2;
    width: 100%;
  }
`;

export const VariableColumn = styled.div`
  width: 280px;
  flex-shrink: 0;
  height: stretch;

  @media (max-width: 768px) {
    order: 1;
    width: 100%;
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const TabsContainer = styled.div`
  display: flex;
  border-bottom: 2px solid ${({ theme }) => theme.grayLight};
  margin-bottom: 1.5rem;
`;

export const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ theme, $active }) => ($active ? theme.pantone2190C : 'transparent')};
  margin-bottom: -2px;
  padding: 0.5rem 1.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ theme, $active }) =>
    $active ? theme.pantone2190C : theme.grayDark};
  display: flex;
  align-items: center;
  gap: 0.35rem;
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.pantone2190C};
  }
`;

export const DirtyDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.errorLight};
  flex-shrink: 0;
`;


export const CultureTabPanel = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
`;

// ---------------------------------------------------------------------------
// Field label + title input
// ---------------------------------------------------------------------------

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.25rem;
`;

export const FieldLabel = styled.label`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grayDark};
`;

export const TitleInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  font-size: 0.9375rem;
  border: 1px solid ${({ theme }) => theme.grayLight};
  border-radius: 4px;
  background: ${({ theme }) => theme.white};
  color: inherit;
  font-family: inherit;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.pantone2190C};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.pantone2190C + '33'};
  }
`;

// Editor de uma linha para o subject (mesmo visual do TitleInput, com chips de
// variável). Reutiliza o estilo de chip compartilhado.
export const SubjectEditorWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  font-size: 0.9375rem;
  border: 1px solid ${({ theme }) => theme.grayLight};
  border-radius: 4px;
  background: ${({ theme }) => theme.white};
  color: inherit;
  font-family: inherit;

  &:focus-within {
    border-color: ${({ theme }) => theme.pantone2190C};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.pantone2190C + '33'};
  }

  .ProseMirror {
    outline: none;
    font-family: inherit;
    white-space: pre-wrap;
    word-break: break-word;

    p {
      margin: 0;
    }
  }

  ${templateVariableChipCss}
`;

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

export const EditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.grayLight};
  border-radius: 4px;
  overflow: hidden;
  background: ${({ theme }) => theme.white};
`;

export const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.grayLight};
  background: ${({ theme }) => theme.pantone656C};
  flex-wrap: wrap;
`;

export const EditorContent = styled.div`
  min-height: 240px;
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.white};

  .ProseMirror {
    outline: none;
    min-height: 220px;
    font-family: inherit;

    p {
      margin: 0 0 0.5rem 0;
    }

    ul,
    ol {
      padding-left: 1.5rem;
    }

    blockquote {
      border-left: 3px solid ${({ theme }) => theme.grayLight};
      padding-left: 0.75rem;
      color: ${({ theme }) => theme.grayDark};
      margin: 0.5rem 0;
    }

    pre {
      background: ${({ theme }) => theme.pantone656C};
      padding: 0.75rem;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.875rem;
      overflow-x: auto;
    }
  }

  ${templateVariableChipCss}
`;

export const SaveArea = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

export const ColorPickerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  .chrome-picker {
    box-shadow: none !important;
    border-radius: 0 !important;
  }
`;

